import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import '../../scss/menuitem.style.scss';

// Default icons for the menu items
const ChevronDownIcon = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
    <path
      d="M3 4.5L6 7.5L9 4.5"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ChevronUpIcon = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
    <path
      d="M9 7.5L6 4.5L3 7.5"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

interface MenuSubItem {
  id: string | number;
  label: string;
  icon?: React.ReactNode | string;
  to?: string;
  onClick?: () => void;
  isActive?: boolean;
}

interface MenuItemProps {
  item: {
    id: string | number;
    label: string;
    icon?: React.ReactNode | string;
    to?: string;
    onClick?: () => void;
    submenu?: MenuSubItem[];
    isExpanded?: boolean;
    isNew?: boolean;
  };
  isActive?: boolean;
  onSelect?: (item: any) => void;
  onToggleSubmenu?: (id: string | number) => void;
  level?: number;
}

export const MenuItem: React.FC<MenuItemProps> = ({
  item,
  isActive = false,
  onSelect,
  onToggleSubmenu,
  level = 0
}) => {
  const { t } = useTranslation();
  const isExpanded = item.isExpanded || false;
  const hasSubmenu = item.submenu && item.submenu.length > 0;
  const navigate = useNavigate();
  
  const hasActiveSubmenuItem = hasSubmenu && item.submenu?.some(subItem => subItem.isActive);
  const shouldParentBeActive = isActive && !hasActiveSubmenuItem;
  const handleClick = () => {
    if (hasSubmenu) {
      onToggleSubmenu?.(item.id);
    } else {
      onSelect?.(item);
      if (item.onClick) {
        item.onClick();
      }
      if (item.to) {
        navigate(item.to);
      }
    }
  };

  interface HandleSubmenuItemClick {
    (subItem: MenuSubItem): void;
  }

  const handleSubmenuItemClick: HandleSubmenuItemClick = (subItem) => {
    onSelect?.(subItem);
    if (subItem.onClick) {
      subItem.onClick();
    }
    if (subItem.to) {
      navigate(subItem.to);
    }
  };

  return (
    <div className={`menu-item menu-item--level-${level}`}>
      <button
        className={`menu-item__button ${shouldParentBeActive ? 'menu-item__button--active' : ''} ${hasSubmenu ? 'menu-item__button--has-submenu' : ''}`}
        onClick={handleClick}
        data-testid={`menu-item-${item.id}`}
      >
        <div className="menu-item__content">
          {item.icon && (
            <div className="menu-item__icon">
              {typeof item.icon === 'string' ? (
                <img src={item.icon} alt="" className="menu-item__icon-image" />
              ) : (
                item.icon
              )}
            </div>
          )}
          <div className="menu-item__label-wrapper">
            <span className="menu-item__label">{item.label}</span>
            {item.isNew && <span className="menu-item__new">{t('MENU_BADGE_NEW')}</span>}
          </div>
        </div>
        
        {hasSubmenu && (
          <div className="menu-item__chevron">
            {isExpanded ? <ChevronUpIcon /> : <ChevronDownIcon />}
          </div>
        )}
      </button>

      {hasSubmenu && isExpanded && item.submenu && (
        <div className="menu-item__submenu">
          {item.submenu.map((subItem) => (
            <button
              key={subItem.id}
              className={`menu-item__sub-button ${subItem.isActive ? 'menu-item__sub-button--active' : ''}`}
              onClick={() => handleSubmenuItemClick(subItem)}
              data-testid={`submenu-item-${subItem.id}`}
            >
              <div className="menu-item__sub-content">
                {subItem.icon && (
                  <div className="menu-item__sub-icon">
                    {typeof subItem.icon === 'string' ? (
                      <img src={subItem.icon} alt="" className="menu-item__icon-image" />
                    ) : (
                      subItem.icon
                    )}
                  </div>
                )}
                <span className="menu-item__sub-label">{subItem.label}</span>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default MenuItem;
